import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Alert, Switch } from 'antd';
import { saveOdooConfig, getOdooConfig, testOdooConnection, updateOdooConfig } from '../../api/odoo-crm';

const OdooConfigModal = ({ visible, onClose, token }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (visible) {
      loadExistingConfig();
    }
  }, [visible]);

  const loadExistingConfig = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await getOdooConfig(authToken);
      if (response.data) {
        form.setFieldsValue({
          databaseName: response.data.databaseName,
          url: response.data.url,
          email: response.data.email,
          // Don't load password or API key for security
        });
        setIsActive(!!response.data.isActive);
      }
    } catch (error) {
      console.log('No existing config found');
    }
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const payload = { ...values, isActive };
      const existing = await getOdooConfig(authToken);
      if (existing?.data) {
        await updateOdooConfig(payload, authToken);
      } else {
        await saveOdooConfig(payload, authToken);
      }
      message.success('Odoo CRM configuration saved successfully!');
      setConnectionStatus('success');
      onClose();
    } catch (error) {
      message.error('Failed to save configuration: ' + (error.message || 'Unknown error'));
      setConnectionStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await testOdooConnection(authToken);
      if (response.success) {
        message.success('Connection test successful!');
        setConnectionStatus('success');
      } else {
        message.error('Connection test failed');
        setConnectionStatus('error');
      }
    } catch (error) {
      message.error('Connection test failed: ' + (error.message || 'Unknown error'));
      setConnectionStatus('error');
    } finally {
      setTestLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setConnectionStatus(null);
    onClose();
  };

  return (
    <Modal
      title="Odoo CRM Configuration"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Alert
        message="Configure your Odoo CRM credentials to enable automatic contact synchronization with KPIs."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {connectionStatus === 'success' && (
        <Alert
          message="Connection Successful"
          description="Your Odoo CRM is connected and ready to sync contacts."
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {connectionStatus === 'error' && (
        <Alert
          message="Connection Failed"
          description="Please check your credentials and try again."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          databaseName: 'global-frontiers',
          url: 'https://global-frontiers.odoo.com',
          email: 'info@globalfrontiers.io'
        }}
      >
        <Form.Item label="Enable Odoo CRM" valuePropName="checked">
          <Switch checked={isActive} onChange={(v) => setIsActive(v)} />
        </Form.Item>

        <Form.Item
          label="Database Name"
          name="databaseName"
          rules={[{ required: true, message: 'Please enter your Odoo database name' }]}
        >
          <Input placeholder="global-frontiers" />
        </Form.Item>

        <Form.Item
          label="URL"
          name="url"
          rules={[
            { required: true, message: 'Please enter your Odoo URL' },
            { type: 'url', message: 'Please enter a valid URL' }
          ]}
        >
          <Input placeholder="https://global-frontiers.odoo.com" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Please enter your Odoo email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input placeholder="info@globalfrontiers.io" />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          rules={[{ required: true, message: 'Please enter your Odoo password' }]}
        >
          <Input.Password placeholder="Enter your password" />
        </Form.Item>

        <Form.Item
          label="API Key (Optional - for Odoo.com instances)"
          name="apiKey"
          help="For Odoo.com instances, you may need to generate an API key from your user preferences. Leave empty to try with regular password first."
        >
          <Input.Password placeholder="Enter your API key (optional)" />
        </Form.Item>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleTestConnection} 
            loading={testLoading}
            disabled={loading}
          >
            Test Connection
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            disabled={testLoading}
          >
            Save Configuration
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default OdooConfigModal;